import type { Editor } from "@tiptap/react";
import {
  Code2,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListTodo,
  type LucideIcon,
} from "lucide-react";

export type SlashItem = {
  id: string;
  title: string;
  keywords: string[];
  /** Display keys after swapping Mod/Alt/Shift for the current platform. */
  shortcut?: string[];
  icon: LucideIcon;
  apply: (editor: Editor) => void;
};

function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
}

/** Linear-style key glyphs for the menu's trailing column. */
export function slashShortcutGlyphs(keys: string[]): string[] {
  const mac = isMac();
  return keys.map((key) => {
    if (key === "Mod") return mac ? "⌘" : "Ctrl";
    if (key === "Alt") return mac ? "⌥" : "Alt";
    if (key === "Shift") return "⇧";
    return key;
  });
}

export const SLASH_ITEMS: SlashItem[] = [
  {
    id: "heading-1",
    title: "Heading 1",
    keywords: ["h1", "title", "heading"],
    shortcut: ["Mod", "Alt", "1"],
    icon: Heading1,
    apply: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    id: "heading-2",
    title: "Heading 2",
    keywords: ["h2", "heading"],
    shortcut: ["Mod", "Alt", "2"],
    icon: Heading2,
    apply: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    id: "heading-3",
    title: "Heading 3",
    keywords: ["h3", "heading"],
    shortcut: ["Mod", "Alt", "3"],
    icon: Heading3,
    apply: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    id: "bullet-list",
    title: "Bulleted list",
    keywords: ["ul", "unordered", "bullet", "list"],
    shortcut: ["Mod", "Shift", "8"],
    icon: List,
    apply: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    id: "ordered-list",
    title: "Numbered list",
    keywords: ["ol", "ordered", "number", "list"],
    shortcut: ["Mod", "Shift", "9"],
    icon: ListOrdered,
    apply: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    id: "task-list",
    title: "Checklist",
    keywords: ["todo", "task", "check", "checkbox"],
    shortcut: ["Mod", "Shift", "7"],
    icon: ListTodo,
    apply: (editor) => editor.chain().focus().toggleTaskList().run(),
  },
  {
    id: "code-block",
    title: "Code block",
    keywords: ["pre", "fence", "code"],
    shortcut: ["Mod", "Shift", "\\"],
    icon: Code2,
    apply: (editor) => editor.chain().focus().toggleCodeBlock().run(),
  },
];

export function filterSlashItems(query: string): SlashItem[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return SLASH_ITEMS;
  return SLASH_ITEMS.filter((item) => {
    if (item.title.toLowerCase().includes(needle)) return true;
    if (item.id.includes(needle)) return true;
    return item.keywords.some((keyword) => keyword.includes(needle) || needle.includes(keyword));
  });
}
